/**
 * "What matters to you?" — the outcome picker, and the summary it produces.
 *
 * The purpose of this tool is to let someone who lives in a place evaluate a
 * proposal against the things they actually care about. That intent creates two
 * traps, and this module is written around avoiding both.
 *
 * IT MUST NOT PRODUCE A SCORE. Selecting three outcomes invites a roll-up —
 * "your score: 7/10" — and a composite is the one thing this tool refuses,
 * because it turns the argument into one about weighting rather than about the
 * world. The summary below is a list of independent figures. There is no
 * arithmetic across outcomes anywhere in this file, and there must never be.
 *
 * IT MUST NOT HIDE THE BAD NEWS. Selection reorders the page; it never filters
 * it. If choosing outcomes could remove sections, a reader could assemble a
 * view that never shows the finding running against the project, which would
 * quietly gut the commitment to publish it. Everything stays on the page.
 *
 * Outcomes are phrased as a resident would ask them, not as the metric labels
 * them, and each is grouped by the kind of abundance it speaks to.
 */

import { evaluateMetric } from '../engine/evaluate.js';
import type { Category, Metric, Pack, Scenario } from '../engine/schema.js';
import type { Footnotes } from './footnotes.js';
import { esc, num } from './num.js';
import type { AppState } from './store.js';

export interface Outcome {
  id: string;
  /** The question in a resident's words. */
  question: string;
  category: Category;
  /** Metrics that answer it. The first is the headline. */
  metricIds: string[];
  /** Where the fuller treatment lives on the page. */
  anchor: string;
}

export const OUTCOMES: Outcome[] = [
  // ---------------------------------------------------------- economic
  {
    id: 'jobs',
    question: 'How many jobs will it create for my town?',
    category: 'economic',
    metricIds: ['construction_job_years', 'permanent_jobs'],
    anchor: 'area-jobs',
  },
  {
    id: 'training',
    question: 'Will young people get trained here?',
    category: 'economic',
    metricIds: ['construction_apprenticeships'],
    anchor: 'area-jobs',
  },
  {
    id: 'bills',
    question: 'How much could this take off my electricity bill?',
    category: 'economic',
    metricIds: ['bill_savings_monthly', 'bill_savings_percent'],
    anchor: 'area-bills',
  },
  {
    id: 'taxes',
    question: 'What does the town collect in taxes?',
    category: 'economic',
    metricIds: ['net_local_tax'],
    anchor: 'area-taxes',
  },

  // ---------------------------------------------------------- civic
  {
    id: 'teachers',
    question: 'How many teachers could this pay for?',
    category: 'civic',
    metricIds: ['school_teacher_years'],
    anchor: 'area-taxes',
  },
  {
    id: 'townbudget',
    question: 'How big is this next to the town’s own budget?',
    category: 'civic',
    metricIds: ['community_fund_share_of_city'],
    anchor: 'area-taxes',
  },

  // ---------------------------------------------------------- social
  {
    id: 'water',
    question: 'How much water, compared with everything else around here?',
    category: 'social',
    metricIds: ['water_use'],
    anchor: 'water',
  },
  {
    id: 'air',
    question: 'Is the air cleaner or dirtier for building it here?',
    category: 'social',
    metricIds: ['so2_avoided', 'co2e_avoided'],
    anchor: 'air',
  },
  {
    id: 'noise',
    question: 'Will I hear it at night?',
    category: 'social',
    metricIds: ['noise_margin'],
    anchor: 'noise',
  },
  {
    id: 'power',
    question: 'Does it bring its own power, or lean on the grid?',
    category: 'social',
    metricIds: ['capacity_coverage'],
    anchor: 'area-bills',
  },
];

/**
 * Category order and labels.
 *
 * The prose definitions are deliberately empty here and filled from the pack's
 * narrative, so the wording for each kind of abundance is authored once,
 * reviewed, and never invented by the render layer.
 */
export const CATEGORIES: Category[] = ['economic', 'civic', 'social'];

export const CATEGORY_LABEL: Record<Category, string> = {
  economic: 'Economic abundance',
  civic: 'Civic abundance',
  social: 'Social abundance',
};

/**
 * The framework, as approved. Reproduced verbatim — wording, punctuation and
 * all. It is not paraphrased, summarised or re-cased anywhere in the render
 * layer, and it should not be: this is the frame the whole tool is organised
 * around, and a tool that restates its own sponsor's definition loosely invites
 * exactly the argument it is trying to avoid.
 *
 * It lives here rather than in a data pack because it spans jurisdictions. A
 * pack may still override the one-line summary via `narrative.abundance_<id>`
 * if a place needs different emphasis.
 */
export interface AbundancePillar {
  title: string;
  body: string;
}

export const ABUNDANCE: Record<
  Category,
  { tagline: string; rationale: string; pillars: AbundancePillar[] }
> = {
  economic: {
    tagline: 'Affordability, technology and growth to win the future',
    rationale:
      'Economic abundance reduces the desperation that makes civic life untenable. When individuals and families cannot afford homes, childcare, healthcare, transportation, or stable communities, they become trapped in a race for private survival. Scarcity makes people anxious, competitive, transient, tribal and zero-sum. Abundance creates the material room for rootedness, generosity, and public participation.',
    pillars: [
      {
        title: 'Build more of what people need',
        body: 'Regulatory and zoning reform to unlock housing, energy, healthcare, and infrastructure, so America can produce at scale and prices fall through real supply.',
      },
      {
        title: 'Foster innovation, technology, healthy competition',
        body: 'Make it easier to start and grow businesses, and enforce antitrust so gatekeepers can’t manufacture scarcity or block prosperity through predatory tactics.',
      },
    ],
  },
  civic: {
    tagline: 'Spend more of our public money at the local level',
    rationale:
      'Civic abundance gives people agency instead of spectatorship. Early liberal thinkers, like Tocqueville, admired American local government because it taught citizens how to govern by making them do it. A renewed culture of civic abundance should revive that habit: local leaders, mayors, churches, nonprofits, schools, neighborhood groups, business owners, and citizens defining needed outcomes and working to deliver tangible results together. People learn citizenship not by being lectured about democracy or presented with facts, but by practicing responsibility in common projects and delivering tangible results to improve their communities.',
    pillars: [
      {
        title: 'Local self-government that people can feel and trust',
        body: 'Participatory budgeting, decentralized programs, civic engagement pathways, and standing citizen assemblies/town halls to set priorities, deliberate tradeoffs, and legitimize public action.',
      },
      {
        title: 'Limited but effective government',
        body: 'Push resources, authority, and capacity down to states, counties, and towns so decisions match the scale of life. Maintain a short list of essential federal priorities—and execute them well.',
      },
    ],
  },
  social: {
    tagline: 'Strong communities, strong families, rooted in place',
    rationale:
      'Social abundance seeks to rebuild the institutions that form citizens and give connection and purpose to our lives. Families, churches and faith institutions, schools, unions, clubs, civic associations, sports leagues, libraries, public squares, and local newspapers are not decorative. They are where people learn duty, trust, awe, humility, compromise, and belonging. Holistic abundance should include concern for increasing the supply of these formative settings, not only increasing GDP.',
    pillars: [
      {
        title: 'Strong communities as the foundation of human flourishing',
        body: 'Lower cost, more productive opportunity so people have a stable foundation for families and associations—not just survival in isolation.',
      },
      {
        title: 'Build meaningful relationships',
        body: 'Invest in public commons, service pathways, and “third places,” strengthen the associations that cultivate connection—schools, unions/guilds, faith congregations, service corps, neighborhood groups.',
      },
    ],
  },
};

/**
 * The framework in full, for the methodology section. Kept off the main page on
 * purpose: the reader came for Pine Island, and the frame should organise what
 * they see rather than detain them on the way in.
 */
export function abundanceExplainer(): string {
  return `<section class="abundance" id="abundance"><div class="container">
    <h2 class="section-title">Three kinds of abundance</h2>
    <p class="lede">A project can deliver one kind of abundance and cost you another. That trade is the thing worth arguing about.</p>
    <div class="abundance-grid">
      ${CATEGORIES.map(
        (c) => `<article class="abundance-card ${esc(c)}">
          <h3>${esc(CATEGORY_LABEL[c])}</h3>
          <p class="abundance-tagline">${esc(ABUNDANCE[c].tagline)}</p>
          <p class="abundance-rationale">${esc(ABUNDANCE[c].rationale)}</p>
          ${ABUNDANCE[c].pillars
            .map(
              (p) =>
                `<div class="abundance-pillar">
                  <span class="abundance-pillar-t">${esc(p.title)}</span>
                  <p>${esc(p.body)}</p>
                </div>`,
            )
            .join('')}
        </article>`,
      ).join('')}
    </div>
  </div></section>`;
}

/** The coloured tag that carries the framework through the rest of the page. */
export function categoryTag(category: Category): string {
  return `<span class="cat-tag ${esc(category)}">${esc(CATEGORY_LABEL[category])}</span>`;
}

export function outcomeById(id: string): Outcome | undefined {
  return OUTCOMES.find((o) => o.id === id);
}

/** Selected outcomes, in pick order, ignoring ids that no longer exist. */
export function selectedOutcomes(state: AppState): Outcome[] {
  return state.outcomes
    .map((id) => outcomeById(id))
    .filter((o): o is Outcome => Boolean(o));
}

// ---------------------------------------------------------------- picker

export function outcomePicker(pack: Pack, state: AppState): string {
  const chosen = new Set(state.outcomes);

  const groups = CATEGORIES.map((c) => {
    const chips = OUTCOMES.filter((o) => o.category === c)
      .map(
        (o) => `<button class="chip ${esc(c)}" role="switch"
            aria-checked="${chosen.has(o.id)}"
            data-outcome="${esc(o.id)}">${esc(o.question)}</button>`,
      )
      .join('');

    // A pack may override the summary line; otherwise the approved tagline.
    const definition = pack.narrative[`abundance_${c}`] || ABUNDANCE[c].tagline;

    return `<div class="pick-group ${esc(c)}">
      <h3 class="pick-group-title">${esc(CATEGORY_LABEL[c])}</h3>
      ${definition ? `<p class="pick-group-def">${esc(definition)}</p>` : ''}
      <div class="chip-row">${chips}</div>
    </div>`;
  }).join('');

  return `<section class="picker" id="matters"><div class="container">
    <h2 class="section-title">What matters to you?</h2>
    <p class="lede">Pick the outcomes you care about. The page reorders to put them first and answers them side by side.</p>
    <div class="pick-groups">${groups}</div>
    ${
      chosen.size
        ? `<button class="assumption-reset" data-clear-outcomes>Clear what I picked</button>`
        : ''
    }
  </div></section>`;
}

// ---------------------------------------------------------------- summary

/**
 * The chosen outcomes, answered under both scenarios.
 *
 * Each row is a separate question with a separate answer. They are deliberately
 * not combined, not weighted and not totalled.
 */
export function outcomeSummary(
  pack: Pack,
  state: AppState,
  fn: Footnotes,
  built: Scenario,
  notBuilt: Scenario,
): string {
  const chosen = selectedOutcomes(state);
  if (!chosen.length) return '';

  const rows = chosen
    .map((o) => {
      const metrics = o.metricIds
        .map((id) => pack.metrics.find((m) => m.id === id))
        .filter((m): m is Metric => Boolean(m));
      if (!metrics.length) return '';

      const answer = (s: Scenario) =>
        metrics
          .map((m) => {
            if (m.appliesTo && !m.appliesTo.includes(s.id)) {
              return `<p class="sum-na">Does not apply if it is not built.</p>`;
            }
            const r = evaluateMetric(pack, m, {
              scenarioId: s.id,
              params: {},
              overrides: state.overrides,
            });
            return `<div class="sum-metric">
              ${num(r, { drawerId: m.id, compact: true })}
              <span class="sum-metric-label">${esc(m.plainLabel)}</span>
            </div>`;
          })
          .join('');

      return `<div class="sum-row">
        <div class="sum-q">
          ${categoryTag(o.category)}
          <p class="sum-question">${esc(o.question)}${fn.ref(metrics[0]!.id)}</p>
          <a class="sum-jump" href="#${esc(o.anchor)}">see the detail</a>
        </div>
        <div class="sum-a ${state.scenarioId === built.id ? 'active' : 'muted'}">${answer(built)}</div>
        <div class="sum-a ${state.scenarioId === notBuilt.id ? 'active' : 'muted'}">${answer(notBuilt)}</div>
      </div>`;
    })
    .join('');

  return `<section class="summary" id="your-answers"><div class="container">
    <h2 class="section-title">Your questions, both ways</h2>
    <div class="sum-heads">
      <div aria-hidden="true"></div>
      <span class="sum-head">${esc(built.label)}</span>
      <span class="sum-head">${esc(notBuilt.label)}</span>
    </div>
    ${rows}
  </div></section>`;
}
